import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Buildings, ArrowRight } from "@phosphor-icons/react";
import { api, errMsg, fmtMoney } from "../lib/api";
import { useT } from "../i18n";

const fmtClp = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);

const SUB_STYLES = {
  authorized: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-blue-50 text-blue-700 border-blue-200",
  paused: "bg-amber-50 text-amber-700 border-amber-200",
  none: "bg-slate-100 text-slate-600 border-slate-200",
};

const Stat = ({ label, value, testId }) => (
  <div>
    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{label}</p>
    <p data-testid={testId} className="font-mono text-lg font-semibold text-slate-900 mt-0.5 truncate">{value}</p>
  </div>
);

export default function OverviewPage() {
  const { t } = useT();
  const [companies, setCompanies] = useState(null);

  useEffect(() => {
    api.get("/admin/overview").then((r) => setCompanies(r.data)).catch(() => setCompanies([]));
  }, []);

  const goTo = async (wsId) => {
    try {
      await api.post("/workspaces/switch", { workspace_id: wsId });
      window.location.href = "/";
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (companies === null) return <div className="p-6 font-mono text-sm text-slate-400">{t("loading")}</div>;

  const totalUsers = companies.reduce((s, c) => s + c.seats, 0);
  const totalPending = companies.reduce((s, c) => s + c.pending_count, 0);
  const monthlyCost = companies.reduce((s, c) => s + (c.subscription?.status === "authorized" ? c.subscription.amount_clp || 0 : 0), 0);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight" data-testid="overview-title">{t("ov_title")}</h1>
        <p className="text-sm text-slate-500">{t("ov_sub")}</p>
      </div>

      <div className="border border-slate-200 grid grid-cols-2 lg:grid-cols-4">
        <div className="px-5 py-4 border-r border-slate-200"><Stat label={t("ov_companies")} value={companies.length} testId="ov-total-companies" /></div>
        <div className="px-5 py-4 lg:border-r border-slate-200"><Stat label={t("ov_total_users")} value={totalUsers} testId="ov-total-users" /></div>
        <div className="px-5 py-4 border-r border-t lg:border-t-0 border-slate-200"><Stat label={t("ov_pending_items")} value={totalPending} testId="ov-total-pending" /></div>
        <div className="px-5 py-4 border-t lg:border-t-0 border-slate-200"><Stat label={t("ov_monthly_cost")} value={fmtClp(monthlyCost)} testId="ov-monthly-cost" /></div>
      </div>

      <div className="grid md:grid-cols-2 gap-5" data-testid="overview-companies-grid">
        {companies.map((c) => {
          const subStatus = c.subscription?.status || "none";
          return (
            <div key={c.workspace_id} data-testid={`overview-company-${c.workspace_id}`} className="border border-slate-200 p-5 space-y-4 hover:border-slate-400 transition-colors duration-150">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <Buildings size={20} className="text-slate-900 shrink-0" />
                  <div className="min-w-0">
                    <p className="font-heading font-bold text-slate-900 truncate">{c.name}</p>
                    <p className="text-[11px] text-slate-400 font-mono uppercase">{t(`plan_${c.plan}`)} · {c.currency}</p>
                  </div>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border shrink-0 ${SUB_STYLES[subStatus] || SUB_STYLES.none}`}>
                  {t(`sub_${subStatus}`)}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-3 border-t border-slate-100 pt-4">
                <Stat label={t("billing_seats")} value={c.seats} />
                <Stat label={t("ov_sub_cost")} value={c.subscription?.status === "authorized" ? fmtClp(c.subscription.amount_clp) : "—"} />
                <Stat label={t("thisMonth")} value={fmtMoney(c.month_total, c.currency)} />
                <Stat label={t("pendingApproval")} value={c.pending_count} />
                <Stat label={t("pendingAmount")} value={fmtMoney(c.pending_amount, c.currency)} />
                <Stat label={t("approvedReimbursed")} value={fmtMoney(c.approved_amount, c.currency)} />
              </div>

              <button
                data-testid={`overview-goto-${c.workspace_id}`}
                onClick={() => goTo(c.workspace_id)}
                className="w-full border border-slate-300 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors duration-150 py-2 flex items-center justify-center gap-2 text-sm font-medium text-slate-700"
              >
                {t("ov_goto")} <ArrowRight size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
